import type { estypes } from '@elastic/elasticsearch'

import { superGenericPropertyMapping } from './superMetadata/generic'
import { superGithubMetadataMapping } from './superMetadata/github'
import { superJiraMetadataMapping } from './superMetadata/jira'
import { superLinearMetadataMapping } from './superMetadata/linear'
import { superNotionMetadataMapping } from './superMetadata/notion'
import { superSliteMetadataMapping } from './superMetadata/slite'
import { textSubtypes } from './utils'

export const superFragmentsMapping = {
  organizationId: {
    type: 'keyword',
    index: false,
  },
  authorEmails: {
    type: 'keyword',
    index: false,
  },
  createdAt: {
    type: 'date',
    index: false,
  },
  lastEditedAt: {
    type: 'date',
    index: false,
  },
  /**
   * Contain the mirrored permissions extracted from the external source
   * For example for Slack private channel, it's the channel id, for Slack public channel it's the team id
   * For Google Drive it's the email, or the group email or the domain
   */
  audience: {
    type: 'keyword',
    index: false,
  },
  negativeAudience: {
    type: 'keyword',
    index: false,
  },
  parentIdList: {
    type: 'keyword',
    index: false,
  },
  parentTitleList: {
    type: 'keyword',
    index: false,
  },
  breadcrumb: {
    type: 'text',
    ...textSubtypes,
  },
  /**
   * Use to facilitate mapping migration
   */
  mappingHash: { type: 'keyword', index: false },
  indexedAt: { type: 'date', index: false },
  /**
   * id of the indexed unit (google drive file, slack thread...)
   */
  id: { type: 'keyword', index: false },
  url: { type: 'keyword', index: false },
  /** global source name, SLACK, GDRIVE... */
  source: { type: 'keyword', index: false },
  /**
   * type of the content, maybe specific to the source, for example SLACK
   * you have threads, dailies, but you could also have uploaded pdfs or other supported document formats
   */
  type: { type: 'keyword', index: false },
  /**
   * root is the top level parent of the fragment, for SLACK it would be the channel
   */
  rootName: { type: 'keyword', index: false },
  rootId: { type: 'keyword', index: false },
  contentCharactersCount: { type: 'unsigned_long' },

  /**
   * @deprecated use contentCharactersCount instead, you can estimate words count with characters count / 5
   */
  contentWordsCount: { type: 'unsigned_long' },
  /**
   * If the content should be fragmented, it's the index of the fragment inside the original source element
   */
  index: { type: 'unsigned_long' },
  lang: { type: 'keyword', index: false },
  title: { type: 'text', ...textSubtypes },
  subTitle: { type: 'text', ...textSubtypes },
  /**
   * If pertinent, should contain only the words of the content.
   * This field purpose is to be used for classif keywords search.
   */
  content: { type: 'text', ...textSubtypes },
  /**
   * Semantic content can contain description and context of the content
   * It's the semantic content that is vectorized for semantic search
   */
  semanticContent: { type: 'binary' },
  vector: {
    type: 'dense_vector',
    // element_type exist but is not properly typed
    // @ts-ignore
    element_type: 'byte',
    dims: 1024,
    index: true,
    similarity: 'cosine',
  },
  /**
   * If needed, it can contain the original object that could be reused to reproduce the content and semanticContent
   * For Slack, it's the replies array.
   * This can also be used after the retrieval phase to perform other kind of actions (ex: a small sqlite database) or be more oriented to GPT.
   */
  rawContent: { type: 'binary' },
  /**
   * A json array of media URL attached to the fragment
   */
  media: { type: 'binary' },
  /**
   * A version to avoid fetching job if it didn't change
   */
  version: { type: 'keyword', index: false },
  /**
   * A date set on each synchronization, it help to deletes non existent docs
   */
  lastSynchronizationAt: { type: 'date', index: false },
  /**
   * Contain an enum to change the decay rate
   */
  decayRate: { type: 'keyword', index: false },
  /**
   * If the indexed unit had an error during fetching or processing
   * and it's a known error (like Too many chunks in PDF or File too big in GDrive)
   * we can store it here to avoid reprocessing it
   */
  errorMessage: { type: 'keyword', index: false },

  linear: {
    properties: superLinearMetadataMapping,
  },
  github: {
    properties: superGithubMetadataMapping,
  },
  notion: {
    properties: superNotionMetadataMapping,
  },
  jira: {
    properties: superJiraMetadataMapping,
  },
  slite: {
    properties: superSliteMetadataMapping,
  },
  generic: {
    properties: superGenericPropertyMapping,
  },
} satisfies Record<string, estypes.MappingProperty>
